import React, { useState, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, ChevronDown, Loader2, Orbit, Briefcase, Heart, Gem, GraduationCap,
  Users, Flower2, ShieldCheck, MessageCircleQuestion, CalendarClock, Star,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import CosmicBackground from "../components/astro/CosmicBackground";
import BirthForm, { emptyBirth } from "../components/astro/BirthForm";
import UnlockPanel from "../components/astro/UnlockPanel";
import api from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";

const RECENT_KEY = "tt_astro_reports";
const ACCESS_KEY = "tt_astro_access";

const accessTokens = () => {
  try { return JSON.parse(localStorage.getItem(ACCESS_KEY) || "{}"); } catch { return {}; }
};

const readRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
};

const pushRecent = (entry) => {
  const list = [{ ...entry, at: new Date().toISOString() },
                ...readRecent().filter((r) => r.report_id !== entry.report_id)].slice(0, 6);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  return list;
};

const LIFE = [
  ["career", "Career & Profession", Briefcase],
  ["love", "Love & Relationships", Heart],
  ["marriage", "Marriage", Sparkles],
  ["finance", "Finance", Gem],
  ["education", "Education", GraduationCap],
  ["family", "Family", Users],
  ["spiritual", "Spiritual Journey", Flower2],
];

function Skeleton() {
  return (
    <div className="space-y-4" data-testid="report-skeleton">
      <div className="glass rounded-[1.5rem] p-8">
        <div className="flex items-center gap-3 mb-6">
          <Loader2 className="h-5 w-5 animate-spin text-tara-teal" />
          <p className="font-display text-xl text-tara-ink">Calculating your chart and composing the reading…</p>
        </div>
        <div className="space-y-3">
          {[90, 75, 82, 60].map((w, i) => (
            <div key={i} className="h-3 rounded-full bg-tara-sand animate-pulse" style={{ width: `${w}%` }} />
          ))}
        </div>
        <p className="mt-6 text-xs text-tara-muted">
          Planetary positions are computed with the Swiss Ephemeris, then interpreted in the Vedic tradition. This takes a few moments.
        </p>
      </div>
    </div>
  );
}

function Collapsible({ title, subtitle, children, testid, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="glass rounded-2xl overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-4 p-5 text-left">
        <span>
          <span className="block font-display text-lg font-semibold text-tara-ink">{title}</span>
          {subtitle && <span className="block text-xs text-tara-muted mt-0.5">{subtitle}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-tara-teal transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
            <div className="px-5 pb-5 text-sm leading-relaxed text-tara-body">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Kundali() {
  const { lang, t } = useLang();
  const { openBooking } = useBooking();
  const [params] = useSearchParams();
  const [form, setForm] = useState(emptyBirth);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => { setRecent(readRecent()); }, []);

  const abortRef = useRef(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  const restore = async (reportId) => {
    const token = accessTokens()[reportId];
    if (!token) return false;
    try {
      const { data: full } = await api.post("/astro/report/access", { report_id: reportId, access_token: token });
      setData(full);
      const entry = readRecent().find((r) => r.report_id === reportId);
      if (entry) setForm((f) => ({ ...f, name: entry.name || f.name }));
      setTimeout(() => document.getElementById("astro-report")?.scrollIntoView({ behavior: "smooth" }), 250);
      return true;
    } catch {
      const map = accessTokens();
      delete map[reportId];
      localStorage.setItem(ACCESS_KEY, JSON.stringify(map));
      return false;
    }
  };

  // Returning from the payment success page (or a saved report) re-opens it without re-entering the code.
  useEffect(() => {
    const reportId = params.get("report");
    if (!reportId) return;
    (async () => {
      const ok = await restore(reportId);
      if (ok) return;
      const entry = readRecent().find((r) => r.report_id === reportId);
      setData({ report_id: reportId, report_ref: entry?.report_ref || "", locked: true, needs_code: true });
      if (entry) setForm((f) => ({ ...f, name: entry.name || f.name }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const generate = async () => {
    setLoading(true);
    setData(null);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    try {
      const r = await api.post("/astro/kundali", { ...form, lang }, { signal: abortRef.current.signal });
      setData(r.data);
      setRecent(pushRecent({ report_id: r.data.report_id, report_ref: r.data.report_ref, name: form.name }));
      setTimeout(() => document.getElementById("astro-report")?.scrollIntoView({ behavior: "smooth" }), 250);
    } catch (e) {
      if (e?.code === "ERR_CANCELED") return;
      toast.error(e?.response?.data?.detail || "Could not generate the reading. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onUnlocked = (payload) => {
    if (payload.access_token) {
      localStorage.setItem(ACCESS_KEY, JSON.stringify({ ...accessTokens(), [payload.report_id]: payload.access_token }));
    }
    setData((d) => ({ ...(d || {}), ...payload, locked: false, needs_code: false }));
    setRecent(pushRecent({ report_id: payload.report_id, report_ref: payload.report_ref, name: form.name, unlocked: true }));
  };

  const chart = data?.chart;
  const reading = data?.reading;

  return (
    <div className="relative" data-testid="kundali-page">
      <Seo
        title="Free AI Kundali & Birth Chart Analysis"
        description="Generate a complete Vedic Kundali from your name, date, exact time and place of birth — Lagna, planetary positions, 12 houses, Vimshottari Dasha timeline, dosha analysis and traditional remedies, interpreted by AI."
        path="/astrology/kundali"
        keywords={["free Kundali online", "birth chart analysis", "Lagna calculator", "Vimshottari Dasha", "Mangal Dosha check"]}
      />
      <CosmicBackground />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        <div className="grid lg:grid-cols-[1fr_1.05fr] gap-10 items-start">
          <Reveal variant="blur">
            <span className="badge-gold mb-6 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-serif uppercase tracking-[0.28em]">
              <Orbit className="h-3.5 w-3.5" /> AI Kundali
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold leading-[1.06] mb-5">
              <span className="text-gradient-flame">Your Kundali, read with care</span>
            </h1>
            <p className="text-base text-tara-body leading-relaxed mb-6">
              Your birth details are converted into a true sidereal chart with the Swiss Ephemeris (Lahiri ayanamsa),
              then interpreted in the Vedic tradition — personality, all twelve houses, life areas, your running
              Dasha and traditional remedies.
            </p>
            <ul className="space-y-2.5 text-sm text-tara-body">
              {["Exact Lagna, Rashi, Nakshatra and pada", "Nine planets with sign, house, degree and dignity",
                "Vimshottari Dasha timeline with the current Antardasha", "Calm, non-alarming dosha reading",
                "Traditional remedies — mantra, charity, temple worship"].map((x) => (
                <li key={x} className="flex items-start gap-2.5">
                  <Star className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tara-teal fill-current" /> {x}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal variant="scale" delay={0.08}>
            <BirthForm value={form} onChange={setForm} onSubmit={generate} loading={loading} idPrefix="birth" />
            {recent.length > 0 && (
              <div className="glass mt-4 rounded-[1.4rem] p-5" data-testid="recent-reports">
                <p className="mb-3 text-[11px] uppercase tracking-[0.18em] text-tara-muted">Your recent reports</p>
                <div className="flex flex-wrap gap-2">
                  {recent.map((r) => (
                    <button key={r.report_id} onClick={() => restore(r.report_id).then((ok) => {
                      if (!ok) setData({ report_id: r.report_id, report_ref: r.report_ref, locked: true, needs_code: true });
                    })}
                      className="rounded-full border border-tara-teal/25 bg-white/70 px-4 py-2 text-left text-[11px] transition-colors hover:border-tara-teal/60"
                      data-testid={`recent-report-${r.report_id.slice(0, 8)}`}>
                      <span className="font-semibold text-tara-ink">{r.name || "Kundali"}</span>
                      <span className="ml-2 font-mono text-tara-muted">{r.report_ref}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-[10px] text-tara-muted leading-relaxed">
                  Already paid? Open your report here and enter your unlock code — you never pay twice for the same report.
                </p>
              </div>
            )}
          </Reveal>
        </div>
      </section>

      <section id="astro-report" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {loading && <Skeleton />}

        {data && !chart && data.needs_code && (
          <div className="space-y-4" data-testid="report-reopen">
            <p className="text-center text-sm text-tara-body">
              Enter your unlock code to reopen this report{data.report_ref ? ` (${data.report_ref})` : ""}.
            </p>
            <UnlockPanel report={{ report_id: data.report_id, report_ref: data.report_ref, name: form.name }}
                         lang={lang} onUnlocked={onUnlocked} />
          </div>
        )}

        {chart && (
          <div className="space-y-6" data-testid="astro-report">
            {/* Overview + chart summary bento */}
            <div className="grid gap-5 lg:grid-cols-[1.5fr_1fr]">
              <div className="glass-liquid relative overflow-hidden rounded-[1.6rem] p-7" data-testid="report-overview">
                <span className="orb orb-red h-48 w-48 -top-16 -right-10" />
                <div className="relative">
                  <span className="text-[11px] font-serif uppercase tracking-[0.24em] text-tara-tealDeep">Quick life overview</span>
                  <h2 className="font-serif text-2xl font-bold text-tara-ink mt-2 mb-4">{form.name}</h2>
                  <p className="font-display text-lg leading-relaxed text-tara-ink">{data.free.overview}</p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-5">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.16em] text-tara-muted mb-2">Strengths</p>
                      <ul className="space-y-1.5">
                        {(data.free.strengths || []).map((s) => (
                          <li key={s} className="text-sm text-tara-body flex gap-2"><span className="text-tara-teal">◆</span>{s}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.16em] text-tara-muted mb-2">Areas to nurture</p>
                      <ul className="space-y-1.5">
                        {(data.free.focus_areas || []).map((s) => (
                          <li key={s} className="text-sm text-tara-body flex gap-2"><span className="text-tara-vermilion">◆</span>{s}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass rounded-[1.6rem] p-7" data-testid="report-chart-summary">
                <span className="text-[11px] font-serif uppercase tracking-[0.24em] text-tara-tealDeep">Birth chart summary</span>
                <dl className="mt-5 space-y-4">
                  {[
                    ["Ascendant (Lagna)", `${chart.ascendant.sign_sa} · ${chart.ascendant.sign} ${chart.ascendant.degree}`],
                    ["Moon sign (Rashi)", `${chart.moon_sign.sign_sa} · ${chart.moon_sign.sign}`],
                    ["Sun sign", `${chart.sun_sign.sign_sa} · ${chart.sun_sign.sign}`],
                    ["Nakshatra", `${chart.nakshatra.name} — pada ${chart.nakshatra.pada}`],
                    ["Nakshatra lord", chart.nakshatra.lord],
                    ["Birth tithi", chart.panchang.tithi],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-start justify-between gap-4 border-b border-tara-teal/10 pb-3">
                      <dt className="text-[11px] uppercase tracking-[0.14em] text-tara-muted">{k}</dt>
                      <dd className="text-sm font-semibold text-tara-ink text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            {/* Planets */}
            <div className="glass rounded-[1.6rem] p-7 overflow-x-auto" data-testid="report-planets">
              <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-tealDeep mb-5">Planetary positions</h3>
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.14em] text-tara-muted">
                    {["Planet", "Sign", "House", "Degree", "Nakshatra", "State"].map((h) => (
                      <th key={h} className="pb-3 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {chart.planets.map((p) => (
                    <tr key={p.name} className="border-t border-tara-teal/10 hover:bg-tara-teal/[0.04] transition-colors" data-testid={`planet-row-${p.name.toLowerCase()}`}>
                      <td className="py-3 font-semibold text-tara-ink">{p.name}{p.retrograde && p.name !== "Rahu" && p.name !== "Ketu" ? " ℛ" : ""}</td>
                      <td className="py-3 text-tara-body">{p.sign_sa} <span className="text-tara-muted">({p.sign})</span></td>
                      <td className="py-3 text-tara-body">{p.house}</td>
                      <td className="py-3 text-tara-body">{p.degree}</td>
                      <td className="py-3 text-tara-body">{p.nakshatra.name} <span className="text-tara-muted">· {p.nakshatra.pada}</span></td>
                      <td className="py-3">
                        <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                          p.dignity === "Exalted" ? "border-green-300 bg-green-100 text-green-800"
                          : p.dignity === "Debilitated" ? "border-amber-300 bg-amber-100 text-amber-800"
                          : "border-tara-teal/30 bg-tara-teal/[0.07] text-tara-tealDeep"}`}>
                          {p.dignity}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Dasha timeline (always visible) */}
            <div className="glass-liquid rounded-[1.6rem] p-7" data-testid="report-dasha">
              <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-tealDeep mb-5">Vimshottari Dasha</h3>
              <div className="grid sm:grid-cols-3 gap-4 mb-7">
                {[
                  ["Current Mahadasha", `${chart.dasha.current.planet}`, `${chart.dasha.current.start} → ${chart.dasha.current.end}`],
                  ["Current Antardasha", `${chart.dasha.current_antardasha.planet}`, `${chart.dasha.current_antardasha.start} → ${chart.dasha.current_antardasha.end}`],
                  ["Upcoming Mahadasha", chart.dasha.upcoming?.planet || "—", chart.dasha.upcoming ? `${chart.dasha.upcoming.start} → ${chart.dasha.upcoming.end}` : ""],
                ].map(([label, planet, range]) => (
                  <div key={label} className="rounded-2xl border border-tara-teal/20 bg-white/70 p-4">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-tara-muted">{label}</p>
                    <p className="font-display text-2xl text-tara-ink mt-1">{planet}</p>
                    <p className="text-[11px] text-tara-muted mt-1">{range}</p>
                  </div>
                ))}
              </div>

              <div className="relative pl-4">
                <span className="absolute left-0 top-2 bottom-2 w-[2px] rounded-full bg-gradient-to-b from-tara-teal via-tara-vermilion to-transparent" />
                <ul className="space-y-3">
                  {chart.dasha.timeline.map((m) => {
                    const isCurrent = m.planet === chart.dasha.current.planet && m.start === chart.dasha.current.start;
                    const past = m.end < new Date().toISOString().slice(0, 10);
                    return (
                      <motion.li
                        key={`${m.planet}-${m.start}`}
                        initial={{ opacity: 0, x: 16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                        className={`relative ml-4 rounded-2xl border p-4 ${
                          isCurrent ? "border-tara-vermilion/40 bg-tara-vermilion/[0.06]"
                          : past ? "border-tara-teal/15 bg-white/50 opacity-70" : "border-tara-teal/20 bg-white/70"}`}
                        data-testid={`dasha-${m.planet}-${m.start}`}
                      >
                        <span className={`absolute -left-[1.32rem] top-5 h-3 w-3 rounded-full ${
                          isCurrent ? "bg-tara-vermilion ring-4 ring-tara-vermilion/20" : "bg-tara-teal/60"}`} />
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="font-display text-lg text-tara-ink">
                            {m.planet} <span className="text-xs text-tara-muted">· {m.years} years</span>
                          </p>
                          <p className="text-xs text-tara-muted">{m.start} → {m.end}</p>
                        </div>
                        {isCurrent && <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-tara-sindoor">Running now</p>}
                      </motion.li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* Doshas — always visible, calmly worded */}
            <div className="grid gap-5 sm:grid-cols-2" data-testid="report-doshas">
              {chart.doshas.map((d) => (
                <div key={d.id} className="glass rounded-2xl p-6" data-testid={`dosha-${d.id}`}>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <h4 className="font-display text-lg font-semibold text-tara-ink">{d.name}</h4>
                    <span className={`rounded-full border px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      d.present ? "border-amber-300 bg-amber-100 text-amber-800" : "border-green-300 bg-green-100 text-green-800"}`}>
                      {d.present ? "present" : "not indicated"}
                    </span>
                  </div>
                  <p className="text-sm text-tara-body leading-relaxed">{d.detail}</p>
                  <p className="mt-2.5 text-xs text-tara-muted leading-relaxed">{d.tradition}</p>
                </div>
              ))}
            </div>

            {/* Locked / unlocked deep report */}
            {data.locked ? (
              <UnlockPanel report={{ report_id: data.report_id, report_ref: data.report_ref, name: form.name }}
                           lang={lang} onUnlocked={onUnlocked} />
            ) : reading && (
              <>
                {/* 12 houses */}
                <div data-testid="report-houses">
                  <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-tealDeep mb-4">House-wise analysis</h3>
                  <div className="grid gap-3 lg:grid-cols-2">
                    {(reading.houses || []).map((h, i) => {
                      const hs = chart.houses[h.house - 1];
                      return (
                        <Collapsible
                          key={h.house}
                          title={`${h.house}. ${h.title}`}
                          subtitle={hs ? `${hs.sign_sa} · ${hs.planets.join(", ") || "no planets"}` : ""}
                          testid={`house-${h.house}`}
                          defaultOpen={i === 0}
                        >
                          {h.text}
                        </Collapsible>
                      );
                    })}
                  </div>
                </div>

                {/* Life areas */}
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="report-life">
                  {LIFE.map(([key, label, Icon]) => reading.life?.[key] && (
                    <Reveal key={key} variant="up" className="glass bento-tile frame-gold rounded-[1.5rem] p-6" data-testid={`life-${key}`}>
                      <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-tara-tealDeep to-tara-teal">
                        <Icon className="h-5 w-5 text-white" />
                      </span>
                      <h4 className="font-display text-xl font-semibold text-tara-ink mb-2">{label}</h4>
                      <p className="text-sm text-tara-body leading-relaxed">{reading.life[key]}</p>
                    </Reveal>
                  ))}
                </div>

                {/* Dasha insight + dosha notes */}
                <div className="grid gap-5 lg:grid-cols-2">
                  <div className="glass rounded-[1.5rem] p-6" data-testid="report-dasha-insight">
                    <h4 className="flex items-center gap-2 font-serif text-sm uppercase tracking-[0.18em] text-tara-tealDeep mb-3">
                      <CalendarClock className="h-3.5 w-3.5" /> Your current period
                    </h4>
                    <p className="text-sm text-tara-body leading-relaxed">{reading.dasha_insight}</p>
                  </div>
                  <div className="glass rounded-[1.5rem] p-6" data-testid="report-dosha-notes">
                    <h4 className="flex items-center gap-2 font-serif text-sm uppercase tracking-[0.18em] text-tara-tealDeep mb-3">
                      <ShieldCheck className="h-3.5 w-3.5" /> Traditional dosha reading
                    </h4>
                    <div className="space-y-3">
                      {(reading.dosha_notes || []).map((d, i) => (
                        <p key={i} className="text-sm text-tara-body leading-relaxed">
                          <strong className="text-tara-sindoor">{d.name}:</strong> {d.text}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Remedies */}
                <div className="glass-liquid rounded-[1.6rem] p-7" data-testid="report-remedies">
                  <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-tealDeep mb-5">Traditional remedies</h3>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {(reading.remedies || []).map((r, i) => (
                      <div key={i} className="rounded-2xl border border-tara-teal/20 bg-white/70 p-5" data-testid={`remedy-${i}`}>
                        <p className="font-display text-lg text-tara-ink mb-1.5">{r.title}</p>
                        <p className="text-sm text-tara-body leading-relaxed">{r.text}</p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-5 text-[11px] text-tara-muted leading-relaxed">
                    These are traditional spiritual practices offered for reflection; personal beliefs vary and no
                    outcome is guaranteed. For health, legal or financial matters please consult a qualified professional.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <button onClick={() => openBooking("kundali-reading", `Kundali guidance for ${form.name}`)}
                            className="btn-sacred flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-serif font-semibold"
                            data-testid="report-consult-btn">
                      🙏 Consult a Priest for Personalized Guidance
                    </button>
                    <Link to="/astrology/ask" className="btn-ghost-gold flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-serif font-semibold" data-testid="report-ask-btn">
                      <MessageCircleQuestion className="h-4 w-4" /> Ask the Astrology AI
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
